export const INDICATOR_STATUS = Object.freeze({
  AVAILABLE: "available",
  PLANNED: "planned",
});

export const INDICATOR_CATEGORIES = Object.freeze([
  "Trend & Price",
  "Momentum",
  "Volatility",
  "Volume & Flow",
  "NINA Intelligence",
]);

export const INDICATOR_REGISTRY = Object.freeze([
  {
    id: "vwap",
    name: "VWAP",
    description: "Intraday session VWAP derived from authenticated historical bar VWAP and volume.",
    category: "Trend & Price",
    status: INDICATOR_STATUS.AVAILABLE,
    source: "Authenticated historical bars",
    requirements: ["bar VWAP", "bar volume", "intraday interval"],
    tags: ["vwap", "volume weighted average price", "session", "trend"],
  },
  {
    id: "ema20",
    name: "EMA 20",
    description: "20-period exponential moving average derived from authenticated historical closes.",
    category: "Trend & Price",
    status: INDICATOR_STATUS.AVAILABLE,
    source: "Authenticated historical bars",
    requirements: ["20 authenticated closes"],
    tags: ["ema", "moving average", "trend", "20"],
  },
  {
    id: "sma",
    name: "Simple Moving Average",
    description: "Configurable simple moving average. Calculation and settings UI are reserved for this workspace feature.",
    category: "Trend & Price",
    status: INDICATOR_STATUS.PLANNED,
    source: "Authenticated historical bars",
    requirements: ["authenticated closes", "selected period"],
    tags: ["sma", "moving average", "trend"],
  },
  {
    id: "macd",
    name: "MACD",
    description: "Momentum and trend relationship using authenticated historical closes.",
    category: "Momentum",
    status: INDICATOR_STATUS.PLANNED,
    source: "Authenticated historical bars",
    requirements: ["authenticated closes", "configured EMA periods"],
    tags: ["macd", "momentum", "ema", "signal"],
  },
  {
    id: "rsi",
    name: "RSI",
    description: "Relative Strength Index calculated only when sufficient authenticated closes are available.",
    category: "Momentum",
    status: INDICATOR_STATUS.PLANNED,
    source: "Authenticated historical bars",
    requirements: ["authenticated closes", "selected period"],
    tags: ["rsi", "momentum", "relative strength"],
  },
  {
    id: "bollinger",
    name: "Bollinger Bands",
    description: "Configurable price bands derived from authenticated historical closes and standard deviation.",
    category: "Volatility",
    status: INDICATOR_STATUS.PLANNED,
    source: "Authenticated historical bars",
    requirements: ["authenticated closes", "selected period", "deviation setting"],
    tags: ["bollinger", "bands", "volatility", "standard deviation"],
  },
  {
    id: "atr",
    name: "ATR",
    description: "Average True Range for authenticated OHLC volatility analysis.",
    category: "Volatility",
    status: INDICATOR_STATUS.PLANNED,
    source: "Authenticated historical bars",
    requirements: ["authenticated OHLC", "selected period"],
    tags: ["atr", "average true range", "volatility", "risk"],
  },
  {
    id: "volume",
    name: "Volume",
    description: "Authenticated historical bar volume display and future configurable volume studies.",
    category: "Volume & Flow",
    status: INDICATOR_STATUS.PLANNED,
    source: "Authenticated historical bars",
    requirements: ["bar volume"],
    tags: ["volume", "liquidity", "participation"],
  },
  {
    id: "nina-smartflow",
    name: "NINA SmartFlow™",
    description: "Future AninaTrade composite flow intelligence. It remains unavailable until its required verified inputs are connected.",
    category: "NINA Intelligence",
    status: INDICATOR_STATUS.PLANNED,
    source: "Multiple verified AninaTrade inputs",
    requirements: ["verified component signals", "data-quality rules", "Safety Governor rules"],
    tags: ["nina", "smartflow", "flow", "institutional", "liquidity"],
  },
]);

export function getIndicatorDefinition(indicatorId) {
  return INDICATOR_REGISTRY.find((indicator) => indicator.id === indicatorId) || null;
}

export function searchIndicators(query = "", { category = "all", favorites = [] } = {}) {
  const normalizedQuery = String(query).trim().toLowerCase();
  const favoriteSet = new Set(favorites);

  return INDICATOR_REGISTRY.filter((indicator) => {
    if (category === "favorites" && !favoriteSet.has(indicator.id)) return false;
    if (category !== "all" && category !== "favorites" && indicator.category !== category) return false;
    if (!normalizedQuery) return true;

    const searchable = [
      indicator.name,
      indicator.description,
      indicator.category,
      indicator.source,
      ...indicator.requirements,
      ...indicator.tags,
    ]
      .join(" ")
      .toLowerCase();

    return searchable.includes(normalizedQuery);
  });
}

export function canActivateIndicator(indicatorId) {
  return getIndicatorDefinition(indicatorId)?.status === INDICATOR_STATUS.AVAILABLE;
}
